import { HomeSection } from '../types';
import { getIcon } from '../../../utils/icons';
import type { LucideIcon } from 'lucide-react';

interface DynamicSectionProps {
  section: HomeSection;
}

const ToolsSection = ({ section }: { section: HomeSection }) => {
  const gridCols = section.grid?.columns || 6;
  const gap = section.grid?.gap || 4;
  const gapRem = `${gap * 0.25}rem`; // Convert to rem

  return (
    <div className="bg-[var(--appvia-background-paper)] rounded-lg shadow-[var(--appvia-shadow-2)] p-6 border border-[var(--appvia-divider)]">
      <h2 className="text-xl font-semibold mb-4 text-[var(--appvia-text-primary)]">
        {section.title}
      </h2>
      <div
        className="grid"
        style={{
          gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`,
          gap: gapRem,
        }}
      >
        {section.items?.map((item, index) => {
          const Icon = item.icon ? (getIcon(item.icon) as LucideIcon) : null;

          return (
            <a
              key={index}
              href={item.url || '#'}
              target={item.openInNewTab ? '_blank' : undefined}
              rel={item.openInNewTab ? 'noopener noreferrer' : undefined}
              className="flex flex-col items-center p-3 hover:bg-[var(--appvia-background-default)] rounded-lg transition-colors"
            >
              <div
                className={`w-16 h-16 ${
                  item.color || 'bg-gray-500'
                } rounded-xl flex items-center justify-center text-white mb-3 flex-shrink-0`}
              >
                {Icon && <Icon className="w-8 h-8" />}
              </div>
              <span className="text-sm text-[var(--appvia-text-primary)] text-center leading-tight">
                {item.title}
              </span>
            </a>
          );
        })}
      </div>
    </div>
  );
};

const LinksSection = ({ section }: { section: HomeSection }) => {
  const gridCols = section.grid?.columns || 2;
  const gap = section.grid?.gap || 4;
  const gapRem = `${gap * 0.25}rem`; // Convert to rem

  return (
    <div className="bg-[var(--appvia-background-paper)] rounded-lg shadow-[var(--appvia-shadow-2)] p-6 border border-[var(--appvia-divider)]">
      <h2 className="text-xl font-semibold mb-4 text-[var(--appvia-text-primary)]">
        {section.title}
      </h2>
      <div
        className="grid"
        style={{
          gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`,
          gap: gapRem,
        }}
      >
        {section.items?.map((item, index) => (
          <a
            key={index}
            href={item.url || '#'}
            target={item.openInNewTab ? '_blank' : undefined}
            rel={item.openInNewTab ? 'noopener noreferrer' : undefined}
            className="flex items-start p-4 hover:bg-[var(--appvia-background-default)] rounded-lg transition-colors"
          >
            {item.icon && (
              <span className="text-2xl mr-3 flex-shrink-0">{item.icon}</span>
            )}
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-[var(--appvia-text-primary)]">
                {item.title}
              </h3>
              {item.description && (
                <p className="text-sm text-[var(--appvia-text-secondary)]">
                  {item.description}
                </p>
              )}
            </div>
          </a>
        ))}
      </div>
    </div>
  );
};

const CustomSection = ({ section }: { section: HomeSection }) => {
  return (
    <div className="bg-[var(--appvia-background-paper)] rounded-lg shadow-[var(--appvia-shadow-2)] p-6 border border-[var(--appvia-divider)]">
      <h2 className="text-xl font-semibold mb-4 text-[var(--appvia-text-primary)]">
        {section.title}
      </h2>
      <p className="text-[var(--appvia-text-secondary)]">
        Custom section: {section.id}
      </p>
    </div>
  );
};

export const DynamicSection = ({ section }: DynamicSectionProps) => {
  if (!section.enabled) {
    return null;
  }

  switch (section.type) {
    case 'tools':
      return <ToolsSection section={section} />;
    case 'links':
      return <LinksSection section={section} />;
    case 'custom':
      return <CustomSection section={section} />;
    default:
      return null;
  }
};
